import type { LeDataContentBlock } from '~/types/leData';
import type { CardLens, LensCard, LensCardType } from '~/types/leLens';

const PRESENTATION_TYPES = new Set<LensCardType>(['table', 'bar', 'drivers', 'pie', 'line', 'area', 'metric']);

export type LensPresentationEdit = {
  lensId: string;
  baseVersion: number;
  cardId: string;
  type: LensCardType;
  x?: string;
  value?: string;
};

export function parseLensPresentationEdits(blocks: Array<LeDataContentBlock>) {
  const proposals: Array<LensPresentationEdit> = [];
  const cleaned = blocks.map((block) => {
    if (block.type !== 'text') {
      return block;
    }
    const payload = { ...block.payload };
    for (const key of ['markdown', 'text', 'content', 'message', 'body']) {
      if (typeof payload[key] !== 'string') {
        continue;
      }
      payload[key] = payload[key].replace(/```lens-edit\s*([\s\S]*?)(?:```|$)/g, (_match, json) => {
        try {
          const edit = JSON.parse(json);
          if (
            typeof edit.lensId === 'string' &&
            typeof edit.cardId === 'string' &&
            Number.isInteger(edit.baseVersion) &&
            PRESENTATION_TYPES.has(edit.type) &&
            (edit.x === undefined || typeof edit.x === 'string') &&
            (edit.value === undefined || typeof edit.value === 'string')
          ) {
            proposals.push(edit);
          }
        } catch {}
        return '';
      });
    }
    return { ...block, payload };
  });
  return {
    blocks: cleaned,
    proposals: proposals.filter(
      (edit, index) =>
        proposals.findIndex((other) => other.cardId === edit.cardId && other.lensId === edit.lensId) === index,
    ),
  };
}

export function applyLensPresentationEdit(lens: CardLens, edit: LensPresentationEdit): LensCard | undefined {
  if (lens.id !== edit.lensId || lens.version !== edit.baseVersion || !PRESENTATION_TYPES.has(edit.type)) {
    return undefined;
  }
  const card = lens.spec.cards.find((entry) => entry.id === edit.cardId);
  const dataset = lens.spec.datasets?.find((entry) => entry.id === card?.datasetId);
  if (!card || !dataset || card.type === 'insight') {
    return undefined;
  }
  const view = {
    ...card.view,
    ...(edit.x !== undefined ? { x: edit.x } : {}),
    ...(edit.value !== undefined ? { value: edit.value } : {}),
  };
  const fields = new Set([
    ...(dataset.columns ?? []).map((column) => column.name),
    ...(dataset.fields ?? []).map((field) => field.name),
  ]);
  if (edit.x !== undefined && !fields.has(edit.x)) {
    return undefined;
  }
  if (edit.value !== undefined && !fields.has(edit.value)) {
    return undefined;
  }
  if (edit.type !== 'table' && (!view.value || (edit.type !== 'metric' && !view.x))) {
    return undefined;
  }
  return { ...card, type: edit.type, view };
}
